import { useCallback, useEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useAppEnvironmentStore } from '../../../stores/AppEnvironment/appEnvironment.store';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import { WithId } from '../../../types/Base.types';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { BrandIntegration } from '../../context/ApplicationContext/applicationContext.types';
import { useTranslation } from '../../translation/TranslationContext';
import { Order, StatusType } from '../order.types';
import { OrderView } from '../ordersView.types';
import { getOrderView } from '../ordersView.utils';

type UseOrderURLValidationProps = {
  currentOrder?: Order;
  canEditOrder: boolean;
  brandIntegrations?: BrandIntegration[];
};

function useOrderURLValidation({
  currentOrder,
  canEditOrder,
  brandIntegrations,
}: UseOrderURLValidationProps) {
  const history = useHistory();
  const params = useParams<WithId>();
  const location = useLocation<{ [key: string]: boolean }>();
  const { userRole, team } = useApplicationContext();
  const { translate } = useTranslation();
  const [selectedOrder, handleChangeSelectedOrder] = useOrderBookStore(
    useShallow((state) => [state.selectedOrder, state.handleChangeSelectedOrder]),
  );
  const appEnvironment = useAppEnvironmentStore((state) => state.appEnvironment);

  const orderView = getOrderView({
    canEditOrder,
    role: userRole,
    appEnvironment,
    integrations: brandIntegrations,
    orderStatus: currentOrder?.status as StatusType | undefined,
    orderIntegrationStatus: currentOrder?.synchronized,
  });

  const validateOrderPageURL = useCallback(() => {
    if (!currentOrder?.id || !currentOrder?.status) {
      return;
    }

    const viewPathname = `/orders/${params.id}/${orderView}`;

    if (location.pathname === viewPathname) {
      return;
    }

    history.replace(`${viewPathname}${location.search}${location.hash}`, location.state);
  }, [
    currentOrder?.id,
    currentOrder?.status,
    history,
    location.hash,
    location.pathname,
    location.state,
    location.search,
    orderView,
    params.id,
  ]);

  useEffect(() => {
    validateOrderPageURL();
  }, [validateOrderPageURL]);

  useEffect(() => {
    const mustChangeOrderBookActiveOrder =
      orderView === OrderView.EDIT &&
      currentOrder?.id &&
      selectedOrder?.id !== currentOrder?.id &&
      currentOrder?.status !== 'SUGGESTED';

    if (mustChangeOrderBookActiveOrder) {
      handleChangeSelectedOrder({ order: currentOrder, teamId: team?.id, translate });
    }
  }, [currentOrder?.id]);

  return { orderView };
}

export default useOrderURLValidation;
